import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { LoaderCircleIcon } from 'lucide-react';
import { PageHeader } from '@/modules/common';

import { useNotificationTemplates } from '../api';
import { TemplateSection } from '../components';
import {
  NotificationTemplateResponse,
  TEMPLATE_SECTION_KEYS,
} from '../types';

const formatSectionTitle = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^(.)/, (c) => c.toUpperCase());

export const NotificationTemplatesPage: FC = () => {
  const { isFetching, templates, updateMutation } = useNotificationTemplates();

  const form = useForm<{ templates: NotificationTemplateResponse[] }>({
    values: { templates },
  });

  const onSubmit = async (values: { templates: NotificationTemplateResponse[] }) => {
    await updateMutation.mutateAsync({ updates: values.templates });
    toast.success('Templates updated successfully');
  };

  const isInitialLoading = isFetching && templates.length === 0;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PageHeader
          title="SMS Templates"
          actions={[
            <Button
              key="save"
              type="submit"
              isLoading={updateMutation.isPending}
              disabled={isInitialLoading}
            >
              Save
            </Button>,
          ]}
        />
        {isInitialLoading ? (
          <div className="flex justify-center py-16">
            <LoaderCircleIcon className="animate-spin text-zinc-400" size={32} />
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {TEMPLATE_SECTION_KEYS.map((key) => (
              <TemplateSection
                key={key}
                sectionKey={key}
                title={formatSectionTitle(key)}
                control={form.control}
                templates={templates}
              />
            ))}
          </div>
        )}
      </form>
    </Form>
  );
};
